import { AuditService } from "../services/audit-service";
import { useEffect, useState } from "react";
import { Relation } from "@amzn/ito-client";
import { AuditTrail } from "model";
import { CurrentPage } from "common/utils/config-models";

/* 
 * Pagination of audit trails was implemented in memory (audits already retrieved by the 
 * endpoints), given they might come from two different sources (calls to the endpoints
 * with ham and amznham consumers). Even though the endpoints work with pagination from 
 * the backend, having multiple sources made use that impractical. Furthermore, the amount
 * of audits per order is small enough to load all the entries from a single call to the 
 * backend with a page size of 200.
 */
const MAX_PAGE_SIZE_PER_CONSUMER = 200;
const DEFAULT_PAGE_SIZE = 10;

export interface useGetAuditTrailsProps {
    orderId: string;
    orderConsumer: string;
    orderRelations: Relation[]
}

interface useGetAuditTrailsTableData {
    auditError: Error | null;
    auditTrails: AuditTrail[];
    isLoadingAudit: boolean;
}

interface useGetAuditTrailsTablePagination {
    currentPage: CurrentPage;
    pagesCount: number;
    setCurrentPage: (page: CurrentPage) => void;
    onChangePageHandler: ({ detail }: { detail: any }) => void;
}

export interface useGetAuditTrailsResponse {
    data: useGetAuditTrailsTableData,
    pagination: useGetAuditTrailsTablePagination,
    doGetAuditTrails: () => void
}

export function useGetAuditTrails(props: useGetAuditTrailsProps): useGetAuditTrailsResponse {
    const orderId = props.orderId;
    const orderConsumer = props.orderConsumer;
    const orderRelations = props.orderRelations;
    const [auditError, setAuditError] = useState<Error | null>(null);
    const [auditTrails, setAuditTrails] = useState<AuditTrail[]>([]);
    const [pageAuditTrails, setPageAuditTrails] = useState<AuditTrail[]>([]);
    const [isLoadingAudit, setIsLoadingAudit] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<CurrentPage>({ index: 1, isPageReset: true });

    let pagesCount = Math.ceil(auditTrails.length / DEFAULT_PAGE_SIZE);
    const preferencesKey = "AuditTrailsPreferences";

    /* istanbul ignore next */
    const onChangePageHandler = ({ detail }: { detail: any }) => {
        setCurrentPage({ index: detail.currentPageIndex, isPageReset: false });
    };

    function doGetAuditTrails() {
        setIsLoadingAudit(true);
        AuditService.instance.getAuditTrails({
            orderId, 
            orderConsumer, 
            orderRelations,
            pageSize: MAX_PAGE_SIZE_PER_CONSUMER,
        })
            .then((result: AuditTrail[]) => {
                setIsLoadingAudit(false);
                processAuditResults(result);
            })
            .catch((error: Error) => {
                console.error('Failed to get audit trails:', error);
                setIsLoadingAudit(false);
                setAuditError(error);
            });
    }

    function processAuditResults(results: AuditTrail[]) {
        const sortedAuditsByDate = [...results].sort((a, b) => {
            if (!a.date) return 1;
            if (!b.date) return -1;
            return b.date.getTime() - a.date.getTime();
        });
        setAuditTrails(sortedAuditsByDate);
        setPageAuditTrails(sortedAuditsByDate.slice(0, DEFAULT_PAGE_SIZE));
        pagesCount = Math.ceil(results.length / DEFAULT_PAGE_SIZE);
    }

    function refreshAuditTrails() {
        if(auditTrails.length === 0) {
            doGetAuditTrails();
        } else {
            const startIndex = (currentPage.index - 1) * DEFAULT_PAGE_SIZE;
            const endIndex = startIndex + DEFAULT_PAGE_SIZE;
            setPageAuditTrails(auditTrails.slice(startIndex, endIndex));
        }
    }

    useEffect(() => {
        if (!currentPage.isPageReset) {
          refreshAuditTrails();
        }
      }, [currentPage.index, currentPage.isPageReset]);

    return { 
        data: {
            auditError, 
            auditTrails: pageAuditTrails, 
            isLoadingAudit,
        },
        pagination: {
            currentPage,
            pagesCount,
            setCurrentPage,
            onChangePageHandler
        },
        doGetAuditTrails
    };
}
